import { Link, Outlet } from "@tanstack/react-router";
import { EyeIcon } from "lucide-react";
import { useMemo } from "react";
import type { Me } from "@/api/me";
import { DatanomyLogo } from "@/components/brand";
import { cn } from "@/lib/utils";
import { MeBoundary } from "./MeBoundary";
import { visiblePortalSections } from "./nav";
import { NotificationBell } from "@/notifications/NotificationBell";
import { UserMenu } from "./UserMenu";
import { ViewAsBanner } from "./ViewAsBanner";

/** Minimal layout for B2B guest client users (and staff previewing the portal). */
export function PortalLayout() {
  return <MeBoundary>{(me) => <PortalLayoutInner me={me} />}</MeBoundary>;
}

function clientLabel(me: Me): string | null {
  const names = Array.from(
    new Set(
      me.client_scopes
        .map((s) => [s.client_name, s.department_name].filter(Boolean).join(" · "))
        .filter((n) => n.length > 0),
    ),
  );
  return names.length ? names.join(", ") : null;
}

function PortalLayoutInner({ me }: { me: Me }) {
  const sections = useMemo(() => visiblePortalSections(me.permissions), [me.permissions]);
  const client = clientLabel(me);

  const navLinkClass =
    "flex h-8 items-center gap-2 rounded-md px-2.5 text-[13px] font-medium whitespace-nowrap text-muted-foreground transition-colors outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40";

  return (
    <div className="min-h-dvh bg-background">
      <ViewAsBanner me={me} />
      {me.is_staff && !me.view_as && (
        <div className="flex items-center justify-center gap-2 bg-accent px-4 py-1.5 text-xs font-medium text-accent-foreground">
          <EyeIcon className="size-3.5" aria-hidden="true" />
          You're previewing the client portal as staff.
          <Link to="/" className="underline underline-offset-2">
            Back to Pulse
          </Link>
        </div>
      )}
      <header className="sticky top-0 z-20 border-b bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4 md:px-6">
          <Link to="/portal" className="flex items-center gap-2 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/40">
            <DatanomyLogo className="md:h-8" />
            <span className="hidden border-l pl-2.5 text-[13px] font-medium text-muted-foreground sm:inline">Client Portal</span>
          </Link>
          {client && (
            <span className="hidden truncate rounded-md border bg-card px-2 py-0.5 text-xs text-muted-foreground md:inline">
              {client}
            </span>
          )}
          <div className="ml-auto flex items-center gap-1">
            <NotificationBell />
            <UserMenu me={me} />
          </div>
        </div>
        <nav aria-label="Portal" className="mx-auto max-w-6xl overflow-x-auto px-2 md:px-4">
          <ul className="flex items-center gap-0.5 pb-2">
            {sections.map(({ slug, label, icon: Icon }) => (
              <li key={slug}>
                <Link
                  to="/portal/$section"
                  params={{ section: slug }}
                  className={navLinkClass}
                  activeProps={{ className: cn("!bg-accent !text-accent-foreground"), "aria-current": "page" }}
                >
                  <Icon className="size-4" aria-hidden="true" />
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </header>
      <main id="main" className="mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-8">
        <Outlet />
      </main>
    </div>
  );
}
